import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { type DBCategory, type DBDiscovery, type DBQuiz } from "@/lib/content.functions";
import {
  checkAdmin,
  upsertCategory,
  deleteCategory,
  upsertDiscovery,
  deleteDiscovery,
  upsertQuiz,
  deleteQuiz,
} from "@/lib/admin.functions";
import { bulkDiscoveryAction, getAdminContent, listCollectionsAdmin, adminListComments, adminModerateComment, adminListOrders } from "@/lib/admin-extras.functions";
import { suggestRelated } from "@/lib/qa.functions";
import { listIntelSources, startInstagramConnect } from "@/lib/intel.functions";
import { listSubscribers, sendNewsletterIssue, type Subscriber } from "@/lib/newsletter.functions";
import { getWaitlistStats, type WaitlistStats } from "@/lib/waitlist.functions";
import { usePersistentState } from "@/hooks/use-persistent-state";

import { previewArticleBody, generateArticleBody, generateMissingArticles, generateArticleMeta, generateFullArticle, generateArticleImage, backfillMissingCovers } from "@/lib/ai-content.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Pencil, Trash2, Plus, LogOut, Download, Sparkles, RotateCw, Eye, ImageIcon, Wand2,
  LayoutDashboard, FileText, Folder, Layers, HelpCircle, Users, Bot, BarChart3, Search,
  Image as ImageLucide, Settings as SettingsIcon, Globe, Menu, X, ShieldCheck,
  Calendar as CalendarIcon, Command as CommandIcon, Instagram, Headphones,
  Bell, ShoppingBag, MessageCircle, FileEdit, Rocket, ExternalLink, MoreHorizontal,
  ChevronDown, Brain, Send, TrendingUp,
  Radio,
} from "lucide-react";

import { ImageUrlField } from "@/components/admin/ImageUrlField";
import {
  NextActions, CollectionsPanel, AutomationPanel, AnalyticsPanel, SeoPanel, MediaPanel,
  SubscribersGrowthChart, SettingsPanel,
} from "@/components/admin/AdminPanels";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { QaPanel, EditorialCalendar, SeoOgPreview } from "@/components/admin/QaAndCalendar";
import { AudioQaPanel } from "@/components/admin/AudioQaPanel";
import { AudienceIntelligencePanel } from "@/components/admin/AudienceIntelligencePanel";
import { ReelsCommandCenter } from "@/components/admin/ReelsCommandCenter";
import { SmartasaurusHQ } from "@/components/admin/SmartasaurusHQ";
import { SmartQaPanel } from "@/components/admin/SmartQaPanel";
import { InlineErrorBoundary } from "@/components/admin/InlineErrorBoundary";
import { InstagramMissionControl } from "@/components/admin/InstagramMissionControl";
import { EditorialAssistantHero } from "@/components/admin/EditorialAssistantHero";
import { ManualTaskDock, useManualTasks } from "@/components/admin/ManualTaskDock";
import { DistributionDesk } from "@/components/admin/DistributionDesk";
import { CatalogueIntegrity } from "@/components/admin/CatalogueIntegrity";
import { BroadcastPlanPanel } from "@/components/admin/BroadcastPlanPanel";
import { StoryDeskPanel } from "@/components/admin/StoryDeskPanel";
import { FounderAnalyticsPanel } from "@/components/admin/FounderAnalyticsPanel";
import { PinterestPanel } from "@/components/admin/PinterestPanel";
import { SeoSearchPanel } from "@/components/admin/SeoSearchPanel";
import { StoryKit } from "@/components/admin/StoryKit";


// ============================================================
// HELPERS
// ============================================================
function countWords(s: string | null | undefined) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}
function readingMinutes(words: number) {
  return Math.max(1, Math.ceil(words / 200));
}
function articleStatus(d: { body: string | null; is_published: boolean; publish_at?: string | null }): "missing" | "draft" | "scheduled" | "published" {
  const hasBody = !!(d.body && d.body.trim().length > 0);
  if (!hasBody) return "missing";
  if (!d.is_published) return "draft";
  if (d.publish_at && new Date(d.publish_at).getTime() > Date.now()) return "scheduled";
  return "published";
}
function countSections(body: string) {
  if (!body) return 0;
  const matches = body.match(/^#{1,6}\s+\S/gm);
  return matches ? matches.length : 0;
}
function BodyMeter({ body }: { body: string }) {
  const words = countWords(body);
  const sections = countSections(body);
  let tone: "green" | "yellow" | "red" | "muted" = "muted";
  if (words === 0) tone = "muted";
  else if (words >= 150 && words <= 300) tone = "green";
  else if (words <= 500) tone = "yellow";
  else tone = "red";
  const toneCls = { green: "text-emerald-500", yellow: "text-yellow-500", red: "text-red-500", muted: "text-muted-foreground" }[tone];
  return (
    <div className="mt-2 space-y-1 text-[11px]">
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold ${toneCls}`}>
        <span>{words} words</span>
        <span aria-hidden>·</span>
        <span className={sections > 3 ? "text-red-500" : "text-muted-foreground"}>{sections} section{sections === 1 ? "" : "s"} (max 3)</span>
      </div>
      {words > 0 && (words < 150 || words > 300) && words < 500 && <p className="text-yellow-500">Best between 150–300 words.</p>}
      {words >= 500 && <p className="text-red-500">Too long - cut to 150–300.</p>}
      {sections > 3 && <p className="text-red-500">More than 3 headings.</p>}
    </div>
  );
}

// ============================================================
// ROUTE
// ============================================================
type Section =
  | "overview" | "discoveries" | "collections" | "quizzes" | "categories"
  | "media" | "audience" | "instagram" | "pinterest" | "distribution" | "broadcast" | "analytics" | "comments" | "shop" | "pages"
  | "seo" | "automation" | "smarthq" | "qa" | "settings"
  // URL-addressable, hidden from sidebar
  | "reels" | "calendar" | "audio" | "intel" | "stories" | "integrity";
type AdminSearch = { section: Section; filter?: StatusFilter; sub?: string };

type NavItem = { id: Section; label: string; icon: any; group: "main" | "growth" | "system" };

export const SECTIONS: NavItem[] = [
  { id: "overview",    label: "Overview",           icon: LayoutDashboard, group: "main" },
  { id: "discoveries", label: "Discoveries",        icon: FileText,        group: "main" },
  { id: "collections", label: "Journeys",           icon: Layers,          group: "main" },
  { id: "quizzes",     label: "Quizzes",            icon: HelpCircle,      group: "main" },
  { id: "categories",  label: "Categories",         icon: Folder,          group: "main" },
  { id: "instagram",   label: "Instagram Mission",  icon: Instagram,       group: "growth" },
  { id: "pinterest",   label: "Pinterest Autopilot", icon: Send,            group: "growth" },
  { id: "distribution",label: "Distribution",        icon: TrendingUp,      group: "growth" },
  { id: "broadcast",   label: "Broadcast Plan",     icon: Radio,           group: "growth" },
  { id: "stories",     label: "Story Desk",         icon: Instagram,       group: "growth" },
  { id: "seo",         label: "Search (SEO)",       icon: Search,          group: "growth" },
  { id: "analytics",   label: "Analytics",          icon: BarChart3,       group: "growth" },
  { id: "audience",    label: "Newsletter",         icon: Users,           group: "growth" },
  { id: "comments",    label: "Comments",           icon: MessageCircle,   group: "growth" },
  { id: "shop",        label: "Shop",               icon: ShoppingBag,     group: "growth" },
  { id: "smarthq",     label: "Founder Mode",       icon: Wand2,           group: "system" },
  { id: "automation",  label: "Automation",         icon: Rocket,          group: "system" },
  { id: "qa",          label: "Smart QA",           icon: ShieldCheck,     group: "system" },
  { id: "integrity",   label: "Catalogue Integrity", icon: ShieldCheck,    group: "system" },
  { id: "media",       label: "Images & Galleries", icon: ImageLucide,     group: "system" },

];

const MOBILE_TABS: { id: Section | "more"; label: string; icon: any }[] = [
  { id: "overview",    label: "Overview",  icon: LayoutDashboard },
  { id: "discoveries", label: "Content",   icon: FileText },
  { id: "analytics",   label: "Analytics", icon: BarChart3 },
  { id: "comments",    label: "Comments",  icon: MessageCircle },
  { id: "more",        label: "More",      icon: MoreHorizontal },
];

const ALL_SECTIONS: Section[] = [
  "overview","discoveries","collections","quizzes","categories","media","audience",
  "distribution","broadcast","analytics","comments","shop","pages","seo","automation","smarthq","qa","settings",
  "instagram","pinterest","reels","calendar","audio","intel","stories","integrity",
];

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin · Smartasaurus" }] }),
  validateSearch: (s: Record<string, unknown>): AdminSearch => {
    const sectionRaw = typeof s.section === "string" ? s.section : (s.tab as string | undefined);
    const section = (ALL_SECTIONS.find((x) => x === sectionRaw) ?? "overview") as Section;
    const sub = typeof s.sub === "string" ? s.sub : undefined;
  const filter = typeof s.filter === "string" && ["all", "missing", "draft", "scheduled", "published", "needs-image", "needs-meta", "needs-category", "not-in-collection", "has-reel", "no-reel"].includes(s.filter)
      ? (s.filter as StatusFilter) : undefined;
    return { section, sub, filter };
  },
  component: AdminPage,
});

function AdminPage() {
  const navigate = useNavigate({ from: "/admin" });
  const search = Route.useSearch();
  const taskApi = useManualTasks();
  const contentRequired = search.section === "discoveries" || search.section === "categories" || search.section === "quizzes";
  const check = useServerFn(checkAdmin);
  const { data: admin, isLoading, isError: adminError, error: adminErrorDetail, refetch: retryAdmin } = useQuery({ queryKey: ["check-admin"], queryFn: () => check(), retry: 1 });

  const fetchContent = useServerFn(getAdminContent);
  const { data, isLoading: contentLoading, isError: contentError, error: contentErrorDetail, refetch: retryContent } = useQuery({
    queryKey: ["admin-content"],
    queryFn: () => fetchContent(),
    enabled: admin?.isAdmin === true && contentRequired,
    retry: 1,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserEmail(data.user?.email ?? null));
  }, []);

  async function onSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/admin-login" });
  }

  const go = (section: Section, extras?: Record<string, string>) => {
    navigate({ search: { section, ...(extras as any) } as any });
    setMobileOpen(false);
    setMoreOpen(false);
  };

  function onPaletteNewDiscovery() {
    go("discoveries");
    setTimeout(() => window.dispatchEvent(new CustomEvent("admin:new-discovery")), 30);
  }

  if (isLoading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (adminError) {
    return (
      <div className="min-h-screen bg-background p-10 text-center">
        <h1 className="font-display text-3xl uppercase">Unable to load admin access</h1>
        <p className="mt-3 text-muted-foreground">{adminErrorDetail?.message ?? "The admin check failed."}</p>
        <Button className="mt-6" onClick={() => retryAdmin()}>Try again</Button>
      </div>
    );
  }
  if (!admin?.isAdmin) {
    return (
      <div className="min-h-screen bg-background p-10 text-center">
        <h1 className="font-display text-3xl uppercase">Not an admin</h1>
        <p className="mt-3 text-muted-foreground">Your account is signed in but doesn't have admin access.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button variant="outline" onClick={onSignOut}>Sign out</Button>
          <Button asChild><Link to="/">Back to site</Link></Button>
        </div>
      </div>
    );
  }

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const currentLabel = SECTIONS.find((s) => s.id === search.section)?.label
    ?? ({ instagram: "Instagram", reels: "Reels", calendar: "Calendar", audio: "Audio QA", intel: "Audience Intel" } as Record<string, string>)[search.section]
    ?? "Admin";
  const groups: Array<{ key: "main" | "growth" | "system"; label: string }> = [
    { key: "main",   label: "Content" },
    { key: "growth", label: "Growth" },
    { key: "system", label: "System" },
  ];

  return (
    <div className="admin-scope min-h-screen bg-background">
      <CommandPalette onGo={go} onNewDiscovery={onPaletteNewDiscovery} />

      {/* TOP BAR */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 md:grid-cols-[260px_minmax(0,1fr)_auto] md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" aria-label="Toggle navigation" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link to="/" className="flex min-w-0 items-center gap-2">
              <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-dashed border-primary/50 bg-primary/10 text-primary">
                <Brain className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="notranslate truncate font-display text-sm uppercase tracking-wide leading-none" translate="no">SMARTASAURUS</div>
                <div className="mt-0.5 hidden text-[9px] font-bold uppercase tracking-[0.25em] text-muted-foreground md:block">Admin Dashboard</div>
              </div>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: isMac, ctrlKey: !isMac }))}
            className="hidden h-10 items-center gap-2 rounded-xl border border-border bg-card/60 px-3 text-sm text-muted-foreground transition hover:border-primary/40 hover:text-foreground md:flex"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search discoveries, journeys, quizzes…</span>
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px]">{isMac ? "⌘" : "Ctrl"}K</kbd>
          </button>

          <div className="flex items-center gap-2">
            <Button size="sm" className="hidden md:inline-flex" onClick={onPaletteNewDiscovery}>
              <Plus className="mr-1.5 h-4 w-4" /> Create
            </Button>
            <button
              type="button"
              aria-label="Notifications"
              onClick={() => go("qa")}
              className="relative grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
            >
              <Bell className="h-4 w-4" />
            </button>
            <div className="hidden items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 md:flex">
              <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                {(userEmail?.[0] ?? "A").toUpperCase()}
              </div>
              <div className="text-xs leading-tight">
                <div className="font-semibold">Admin</div>
                <div className="max-w-[120px] truncate text-[10px] text-muted-foreground">{userEmail ?? ""}</div>
              </div>
              <button onClick={onSignOut} title="Sign out" className="ml-1 text-muted-foreground hover:text-foreground">
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border/60 px-4 py-2 md:hidden">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">Admin</div>
            <div className="truncate font-display text-base uppercase leading-tight">{currentLabel}</div>
          </div>
          <Button size="sm" onClick={onPaletteNewDiscovery}>
            <Plus className="mr-1 h-4 w-4" /> Create
          </Button>
        </div>
      </header>

      <div className="flex">
        {mobileOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 top-[57px] z-10 bg-background/60 backdrop-blur-sm md:hidden"
          />
        )}

        {/* SIDEBAR */}
        <aside
          key={mobileOpen ? "mobile-open" : "mobile-closed"}
          className={`${mobileOpen ? "block" : "hidden"} md:block fixed md:sticky top-[57px] z-20 h-[calc(100vh-57px)] w-64 shrink-0 overflow-y-auto overscroll-contain border-r border-border bg-background md:bg-transparent`}
          style={{ WebkitOverflowScrolling: "touch" as any }}
        >
          <nav className="flex h-full flex-col p-3" aria-label="Admin sections">
            {/* Pinned Overview shortcut - always visible at the top of the mobile drawer. */}
            <button
              onClick={() => go("overview")}
              aria-current={search.section === "overview" ? "page" : undefined}
              className={`mb-3 flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition md:hidden ${
                search.section === "overview"
                  ? "border-primary/40 bg-primary/15 font-semibold text-primary"
                  : "border-primary/25 bg-primary/5 text-foreground hover:bg-primary/10"
              }`}
            >
              <LayoutDashboard className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate text-left">Overview</span>
            </button>

            {groups.map((grp) => (
              <div key={grp.key} className="mb-4">
                <div className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground/70">{grp.label}</div>
                <div className="space-y-0.5">
                  {SECTIONS.filter((s) => s.group === grp.key).map((s) => {
                    const Icon = s.icon;
                    const active = search.section === s.id;
                    const isAi = s.id === "smarthq" || s.id === "qa";
                    return (
                      <button
                        key={s.id}
                        onClick={() => go(s.id)}
                        aria-current={active ? "page" : undefined}
                        className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                          active
                            ? "border-primary/25 bg-primary/12 font-semibold text-primary"
                            : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate text-left">{s.label}</span>
                        {isAi && (
                          <span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">AI</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="mt-auto rounded-xl border border-border bg-card p-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Site Status</span>
                <span className="flex items-center gap-1.5 font-semibold text-emerald-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
                </span>
              </div>
              <a href="https://smartasaurus.com" target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-1.5 text-muted-foreground hover:text-primary">
                smartasaurus.com <ExternalLink className="h-3 w-3" />
              </a>
              <Link to="/health" className="mt-2 block font-semibold text-muted-foreground hover:text-primary">System Health →</Link>
              <Link to="/founder" className="mt-1 block font-semibold text-muted-foreground hover:text-primary">Business Overview →</Link>
              <Link to="/" className="mt-1 block text-muted-foreground hover:text-primary">View Website →</Link>
            </div>
          </nav>
        </aside>

        {/* CONTENT */}
        <main className="min-w-0 flex-1 p-4 pb-24 md:p-6 md:pb-6">
          {contentRequired && contentError ? (
            <section className="rounded-xl border border-red-500/30 bg-card p-6">
              <h2 className="font-display text-xl uppercase">Unable to load admin snapshot</h2>
              <p className="mt-2 text-sm text-muted-foreground">Refresh did not change data. The latest admin snapshot failed to load.</p>
              <p className="mt-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">{contentErrorDetail?.message ?? "Unknown loading error"}</p>
              <Button className="mt-4" onClick={() => retryContent()}><RotateCw className="mr-2 h-4 w-4" /> Retry</Button>
            </section>
          ) : contentRequired && (contentLoading || !data) ? (
            <AdminLoading />
          ) : (
            <>
              {search.section === "overview" && (
                <InlineErrorBoundary label="Overview">
                  <SmartasaurusHQ onNavigate={(s, extras) => go(s as Section, extras as any)} taskApi={taskApi} />
                </InlineErrorBoundary>
              )}
              {search.section === "qa" && (
                <InlineErrorBoundary label="Smart QA">
                  <SmartQaPanel taskApi={taskApi} />
                </InlineErrorBoundary>
              )}
              {search.section === "smarthq" && <EditorialAssistantHero onReview={() => go("qa")} taskApi={taskApi} />}
              {search.section === "instagram" && <InstagramMissionControl />}
              {search.section === "pinterest" && <PinterestPanel />}
              {search.section === "seo" && (
                <InlineErrorBoundary label="Search performance">
                  <SeoSearchPanel />
                </InlineErrorBoundary>
              )}
              {search.section === "distribution" && <DistributionDesk />}
              {search.section === "integrity" && <CatalogueIntegrity />}
              {search.section === "stories" && (
                <InlineErrorBoundary label="Story Desk">
                  <StoryDeskPanel />
                </InlineErrorBoundary>
              )}
              {search.section === "broadcast" && (
                <InlineErrorBoundary label="Broadcast Plan">
                  <BroadcastPlanPanel />
                </InlineErrorBoundary>
              )}
              {search.section === "discoveries" && data && (
                <DiscoveriesPanel
                  discoveries={data.discoveries}
                  categories={data.categories}
                  inCollectionIds={(data as any).discoveryIdsInCollections ?? []}
                  reelCoverage={(data as any).reelCoverage}
                  snapshotAt={(data as any).snapshotAt}
                  initialFilter={search.filter ?? "all"}
                />
              )}
              {search.section === "reels" && <ReelsTab />}
              {search.section === "calendar" && <EditorialCalendar />}
              {search.section === "categories" && data && (
                <CategoriesPanel categories={data.categories} discoveries={data.discoveries} />
              )}
              {search.section === "collections" && <CollectionsPanel />}
              {search.section === "quizzes" && data && (
                <QuizzesPanel quizzes={data.quizzes} discoveries={data.discoveries} />
              )}
              {search.section === "audience" && <AudiencePanel />}
              {search.section === "intel" && <AudienceIntelligencePanel />}
              {search.section === "automation" && <AutomationPanel />}
              {search.section === "analytics" && <FounderAnalyticsPanel />}
              {search.section === "seo" && <SeoPanel />}
              {search.section === "media" && <MediaPanel />}
              {search.section === "settings" && <SettingsPanel />}
              {search.section === "audio" && <AudioQaPanel />}
              {search.section === "comments" && <CommentsAdminStub onGo={go} />}
              {search.section === "shop" && <ShopAdminStub />}
              {search.section === "pages" && <PagesAdminStub />}
            </>
          )}
        </main>
      </div>

      {/* MOBILE BOTTOM NAV */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur md:hidden" aria-label="Mobile navigation">
        <div className="grid grid-cols-5">
          {MOBILE_TABS.map((t) => {
            const Icon = t.icon;
            const active = t.id === "more" ? moreOpen : search.section === t.id;
            return (
              <button
                key={t.id}
                onClick={() => (t.id === "more" ? setMoreOpen(true) : go(t.id as Section))}
                className={`flex flex-col items-center gap-1 py-2 text-[10px] font-semibold uppercase tracking-wider ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      {moreOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button aria-label="Close" onClick={() => setMoreOpen(false)} className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-border bg-card p-4 pb-8">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted" />
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-display text-lg uppercase">More</h2>
              <button onClick={() => setMoreOpen(false)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {SECTIONS.filter((s) => !["overview","discoveries","analytics","comments"].includes(s.id)).map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => go(s.id)}
                    className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-background p-3 text-center text-xs">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="truncate">{s.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={onSignOut} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/5 py-3 text-sm font-semibold text-red-500">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      )}
      <ManualTaskDock api={taskApi} />
    </div>
  );
}

// ============================================================
// COMMENTS / SHOP / PAGES - real in-admin panels (no redirects)
// ============================================================
function CommentsAdminStub({ onGo: _onGo }: { onGo: (s: Section) => void }) {
  const fn = useServerFn(adminListComments);
  const mod = useServerFn(adminModerateComment);
  const qc = useQueryClient();
  const [onlyHidden, setOnlyHidden] = useState(false);
  const q = useQuery({
    queryKey: ["admin-comments", onlyHidden],
    queryFn: () => fn({ data: { limit: 100, onlyHidden } }),
    refetchOnWindowFocus: false,
  });
  async function act(commentId: string, action: "hide" | "unhide" | "delete") {
    if (action === "delete" && !confirm("Delete this comment permanently?")) return;
    const t = toast.loading(`${action}…`);
    try {
      await mod({ data: { commentId, action } });
      toast.dismiss(t); toast.success("Done");
      qc.invalidateQueries({ queryKey: ["admin-comments"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); }
  }
  const items: any[] = (q.data as any)?.items ?? [];
  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl uppercase">Comments</h1>
          <p className="text-sm text-muted-foreground">Moderate comments across every discovery.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          <button onClick={() => setOnlyHidden(false)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${!onlyHidden ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>All</button>
          <button onClick={() => setOnlyHidden(true)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${onlyHidden ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Hidden</button>
        </div>
      </header>
      {q.isLoading ? <AdminLoading /> : items.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">No comments yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{c.author?.display_name || c.author?.username || "anon"}</span>
                <span>·</span>
                <span>on</span>
                <span className="truncate font-medium text-foreground">{c.discovery?.title ?? c.discovery_id.slice(0, 8)}</span>
                <span>·</span>
                <span>{new Date(c.created_at).toLocaleString()}</span>
                {c.is_hidden && <span className="rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-yellow-600">Hidden</span>}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm">{c.body}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => act(c.id, c.is_hidden ? "unhide" : "hide")}>
                  {c.is_hidden ? "Unhide" : "Hide"}
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" onClick={() => act(c.id, "delete")}>
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ShopAdminStub() {
  const fn = useServerFn(adminListOrders);
  const q = useQuery({ queryKey: ["admin-orders"], queryFn: () => fn({ data: { limit: 50 } }), refetchOnWindowFocus: false });
  if (q.isLoading || !q.data) return <AdminLoading />;
  const d: any = q.data;
  const fmt = (cents: number, cur = "eur") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur.toUpperCase() }).format((cents ?? 0) / 100);
  return (
    <section className="space-y-4">
      <header>
        <h1 className="font-display text-2xl uppercase">Shop</h1>
        <p className="text-sm text-muted-foreground">Orders and products at a glance.</p>
      </header>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Revenue · 30d</div>
          <div className="mt-1 font-display text-2xl">{fmt(d.totals.revenueCents30)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Paid orders · 30d</div>
          <div className="mt-1 font-display text-2xl">{d.totals.paidCount30}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">All-time orders</div>
          <div className="mt-1 font-display text-2xl">{d.totals.allOrders}</div>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest">Products</h2>
        {d.products.length === 0 ? <p className="text-sm text-muted-foreground">No products yet.</p> : (
          <ul className="divide-y divide-border text-sm">
            {d.products.map((p: any) => (
              <li key={p.id} className="flex items-center justify-between py-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">/{p.slug} · {p.is_active ? "active" : "inactive"}</div>
                </div>
                <div className="tabular-nums">{fmt(p.price_cents, p.currency)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest">Recent orders</h2>
        {d.orders.length === 0 ? <p className="text-sm text-muted-foreground">No orders yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr><th className="py-1 text-left">Invoice</th><th className="text-left">Status</th><th className="text-left">Customer</th><th className="text-right">Total</th><th className="text-right">Date</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {d.orders.map((o: any) => (
                  <tr key={o.id}>
                    <td className="py-2 font-mono text-xs">{o.invoice_number ?? o.id.slice(0, 8)}</td>
                    <td className="text-xs">{o.status}</td>
                    <td className="truncate text-xs">{o.guest_email ?? o.user_id?.slice(0, 8) ?? "-"}</td>
                    <td className="text-right tabular-nums">{fmt(o.total_cents, o.currency)}</td>
                    <td className="text-right text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function PagesAdminStub() {
  const pages = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/newsletter", label: "Newsletter" },
    { path: "/press", label: "Press" },
    { path: "/shop", label: "Shop" },
    { path: "/privacy", label: "Privacy" },
    { path: "/terms", label: "Terms" },
    { path: "/refunds", label: "Refunds" },
  ];
  return (
    <section className="space-y-4">
      <header>
        <h1 className="font-display text-2xl uppercase">Pages</h1>
        <p className="text-sm text-muted-foreground">Static pages on the site. Content lives in the codebase.</p>
      </header>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {pages.map((p) => (
          <li key={p.path} className="flex items-center justify-between px-4 py-3">
            <div>
              <div className="font-medium">{p.label}</div>
              <div className="text-xs text-muted-foreground">{p.path}</div>
            </div>
            <a href={p.path} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Preview <ExternalLink className="h-3 w-3" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}



function AdminLoading() {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-8 w-56" />
      <div className="grid gap-3 md:grid-cols-3"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      <Skeleton className="h-64" />
    </div>
  );
}

// ============================================================
// REELS TAB - editorial inbox for every Instagram reel that feeds
// the Topic Pipeline. Same source as HQ; nothing to reconnect here.
// ============================================================
function ReelsTab() {
  const list = useServerFn(listIntelSources);
  const startConnect = useServerFn(startInstagramConnect);
  const q = useQuery({ queryKey: ["intel-sources"], queryFn: () => list({}) });
  const ig = (q.data as any)?.instagram ?? (q.data as any)?.sources?.find((s: any) => s.type === "instagram");
  const connectMut = useMutation({
    mutationFn: () => (startConnect as any)({}),
    onSuccess: (res: any) => { if (res?.authorizeUrl) window.location.href = res.authorizeUrl; },
    onError: (e: any) => toast.error(e?.message || "Could not start Instagram connection"),
  });
  if (q.isLoading) return <AdminLoading />;
  if (!ig || ig.status !== "connected") {
    return (
      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <Instagram className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="flex-1">
            <h2 className="font-display text-xl uppercase">Instagram not connected</h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Instagram is the discovery engine that feeds every draft. Connecting starts the Meta OAuth flow directly.
            </p>
            <Button size="sm" className="mt-4" onClick={() => connectMut.mutate()} disabled={connectMut.isPending}>
              <Instagram className="mr-2 h-4 w-4" /> {connectMut.isPending ? "Redirecting…" : "Connect Instagram"}
            </Button>
          </div>
        </div>
      </section>
    );
  }
  return <ReelsCommandCenter sourceId={ig.id} />;
}

// ============================================================
// DISCOVERIES - Full CMS with search/sort/filters/bulk/pagination
// ============================================================
type StatusFilter = "all" | "missing" | "draft" | "scheduled" | "published" | "needs-image" | "needs-meta" | "needs-category" | "not-in-collection" | "has-reel" | "no-reel";
type SortKey = "newest" | "oldest" | "title" | "updated";
const PAGE_SIZE = 250;

type AdminReelMatch = {
  id: string;
  title: string | null;
  permalink: string | null;
  published_at: string | null;
  link_status: string | null;
  link_confidence: number | null;
  thumbnail_url: string | null;
};
type AdminDiscovery = DBDiscovery & {
  matched_reel?: AdminReelMatch | null;
  hq_confidence?: number | null;
  quality_score?: number | null;
};
type ReelCoverage = {
  importedReels: number;
  linkedReels: number;
  missingDiscoveryReels: number;
  articleOriginLinks: number;
  uniqueOriginReels: number;
};

function hasMatchedReel(d: AdminDiscovery) {
  return !!(d.hq_origin_reel_id || d.matched_reel);
}

function DiscoveriesPanel({ discoveries, categories, inCollectionIds = [], initialFilter = "all", reelCoverage, snapshotAt }: { discoveries: AdminDiscovery[]; categories: DBCategory[]; inCollectionIds?: string[]; initialFilter?: StatusFilter; reelCoverage?: ReelCoverage; snapshotAt?: string }) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertDiscovery);
  const del = useServerFn(deleteDiscovery);
  const preview = useServerFn(previewArticleBody);
  const genOne = useServerFn(generateArticleBody);
  const genAll = useServerFn(generateMissingArticles);
  const genImg = useServerFn(generateArticleImage);
  const backfillCovers = useServerFn(backfillMissingCovers);
  const bulk = useServerFn(bulkDiscoveryAction);
  const listCols = useServerFn(listCollectionsAdmin);
  const { data: colsData } = useQuery({
    queryKey: ["admin-collections"],
    queryFn: () => listCols(),
    staleTime: 60_000,
  });
  const allCollections = (colsData?.collections ?? []) as Array<{ id: string; title: string; slug: string }>;

  const [editing, setEditing] = useState<Partial<DBDiscovery> | null>(null);
  const [previewState, setPreviewState] = useState<{ open: boolean; loading: boolean; body: string; title: string }>({ open: false, loading: false, body: "", title: "" });
  const [bulkRunning, setBulkRunning] = useState(false);
  const [coverBackfillRunning, setCoverBackfillRunning] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [filter, setFilter] = usePersistentState<StatusFilter>("admin.disc.filter", initialFilter);
  const [q, setQ] = usePersistentState<string>("admin.disc.q", "");
  const [sort, setSort] = usePersistentState<SortKey>("admin.disc.sort", "newest");
  const [categoryFilter, setCategoryFilter] = usePersistentState<string>("admin.disc.cat", "all");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Sync URL ?filter= changes (e.g. when arriving from Next Actions card)
  useEffect(() => { if (initialFilter) setFilter(initialFilter); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [initialFilter]);

  const inCollectionSet = useMemo(() => new Set(inCollectionIds), [inCollectionIds]);

  // Open editor via global event (from command palette)
  useEffect(() => {
    const onNew = () => setEditing({ title: "", excerpt: "", sort_order: 0 });
    window.addEventListener("admin:new-discovery", onNew);
    return () => window.removeEventListener("admin:new-discovery", onNew);
  }, []);

  const save = useMutation({
    mutationFn: ({ d }: { d: any; silent?: boolean }) => upsert({ data: d }),
    onSuccess: (res: any, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] });
      if (vars.silent) { toast.success("Autosaved", { duration: 1500 }); return; }
      if (res?.autoCover?.ok) toast.success("Saved · cover generated");
      else if (res?.autoCover && !res.autoCover.ok) toast.warning(`Saved, auto-cover failed: ${res.autoCover.error}`);
      else toast.success("Saved");
      setEditing(null);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e.message),
  });

  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const filtered = useMemo(() => {
    let rows = discoveries.slice();
    // category filter
    if (categoryFilter !== "all") {
      if (categoryFilter === "none") rows = rows.filter((d) => !d.category_id);
      else rows = rows.filter((d) => d.category_id === categoryFilter);
    }
    // status filter
    if (filter === "missing" || filter === "draft" || filter === "scheduled" || filter === "published") {
      rows = rows.filter((d) => articleStatus(d) === filter);
    } else if (filter === "needs-image") {
      rows = rows.filter((d) => !d.image_url || !d.image_url.trim());
    } else if (filter === "needs-meta") {
      rows = rows.filter((d) => !d.seo_title?.trim() || !d.seo_description?.trim());
    } else if (filter === "needs-category") {
      rows = rows.filter((d) => !d.category_id);
    } else if (filter === "not-in-collection") {
      rows = rows.filter((d) => d.is_published && !inCollectionSet.has(d.id));
    } else if (filter === "has-reel") {
      rows = rows.filter(hasMatchedReel);
    } else if (filter === "no-reel") {
      rows = rows.filter((d) => !hasMatchedReel(d));
    }
    // search
    if (q.trim()) {
      const term = q.toLowerCase();
      rows = rows.filter((d) => d.title.toLowerCase().includes(term) || d.slug.toLowerCase().includes(term) || (d.excerpt ?? "").toLowerCase().includes(term) || (d.matched_reel?.title ?? "").toLowerCase().includes(term));
    }
    // sort
    rows.sort((a, b) => {
      if (sort === "newest") return (b.created_at ?? "").localeCompare(a.created_at ?? "");
      if (sort === "oldest") return (a.created_at ?? "").localeCompare(b.created_at ?? "");
      if (sort === "title") return a.title.localeCompare(b.title);
      return (b.created_at ?? "").localeCompare(a.created_at ?? ""); // updated proxy
    });
    return rows;
  }, [discoveries, filter, categoryFilter, q, sort, inCollectionSet]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const counts = useMemo(() => ({
    all: discoveries.length,
    missing: discoveries.filter((d) => articleStatus(d) === "missing").length,
    draft: discoveries.filter((d) => articleStatus(d) === "draft").length,
    scheduled: discoveries.filter((d) => articleStatus(d) === "scheduled").length,
    published: discoveries.filter((d) => articleStatus(d) === "published").length,
    "needs-image": discoveries.filter((d) => !d.image_url || !d.image_url.trim()).length,
    "needs-meta": discoveries.filter((d) => !d.seo_title?.trim() || !d.seo_description?.trim()).length,
    "needs-category": discoveries.filter((d) => !d.category_id).length,
    "not-in-collection": discoveries.filter((d) => d.is_published && !inCollectionSet.has(d.id)).length,
    "has-reel": discoveries.filter(hasMatchedReel).length,
    "no-reel": discoveries.filter((d) => !hasMatchedReel(d)).length,
  } as Record<StatusFilter, number>), [discoveries, inCollectionSet]);

  const missingCount = counts.missing;
  const missingCoverCount = counts["needs-image"];
  const snapshotLabel = snapshotAt ? new Date(snapshotAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;

  async function onGenerateImage(d: DBDiscovery) {
    const subject = d.excerpt?.trim() || d.title;
    setImageId(d.id);
    const t = toast.loading("Generating cover (~20s)…");
    try {
      await genImg({ data: { discoveryId: d.id, subjectPrompt: subject } });
      toast.dismiss(t); toast.success("Image generated");
      qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); } finally { setImageId(null); }
  }
  async function onPreview(d: DBDiscovery) {
    setPreviewState({ open: true, loading: true, body: "", title: d.title });
    try {
      const cat = catById.get(d.category_id ?? "")?.label ?? null;
      const r = await preview({ data: { title: d.title, excerpt: d.excerpt ?? "", category: cat } });
      setPreviewState({ open: true, loading: false, body: r.body, title: d.title });
    } catch (e: any) { setPreviewState({ open: false, loading: false, body: "", title: "" }); toast.error(e.message); }
  }
  async function onGenerate(d: DBDiscovery, overwrite: boolean) {
    if (overwrite && !confirm(`Overwrite body for "${d.title}"?`)) return;
    setGeneratingId(d.id);
    try { await genOne({ data: { discoveryId: d.id, overwrite } }); toast.success("Generated"); qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); }
    catch (e: any) { toast.error(e.message); } finally { setGeneratingId(null); }
  }
  async function onBulk() {
    if (!confirm(`Generate articles for ${missingCount} missing bodies? ~${Math.ceil(missingCount * 4 / 60)} min.`)) return;
    setBulkRunning(true);
    const t = toast.loading(`Generating ${missingCount}…`);
    try {
      const r = await genAll({});
      toast.dismiss(t);
      r.failed.length === 0 ? toast.success(`Generated ${r.generated}/${r.total}`) : toast.warning(`Generated ${r.generated}/${r.total}, ${r.failed.length} failed`);
      qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); } finally { setBulkRunning(false); }
  }
  async function onBackfillCovers() {
    if (!confirm(`Generate covers for ${missingCoverCount}? ~${Math.ceil(missingCoverCount * 20 / 60)} min.`)) return;
    setCoverBackfillRunning(true);
    const t = toast.loading(`Generating ${missingCoverCount} covers…`);
    try {
      const r = await backfillCovers({});
      toast.dismiss(t);
      r.failed.length === 0 ? toast.success(`Generated ${r.generated}/${r.total}`) : toast.warning(`Generated ${r.generated}/${r.total}, ${r.failed.length} failed`);
      qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); } finally { setCoverBackfillRunning(false); }
  }

  function toggleSelect(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleSelectAll() {
    if (pageRows.every((r) => selected.has(r.id))) {
      const next = new Set(selected); pageRows.forEach((r) => next.delete(r.id)); setSelected(next);
    } else {
      const next = new Set(selected); pageRows.forEach((r) => next.add(r.id)); setSelected(next);
    }
  }
  async function runBulk(action: "publish" | "unpublish" | "delete" | "set_category" | "add_to_collection", arg?: string | null) {
    const ids = [...selected];
    if (ids.length === 0) return;
    const label =
      action === "set_category" ? `set category for ${ids.length}` :
      action === "add_to_collection" ? `add ${ids.length} to collection` :
      `${action} ${ids.length}`;
    if (action === "delete" && !confirm(`Delete ${ids.length} discoveries? This cannot be undone.`)) return;
    setBulkBusy(true);
    const t = toast.loading(`Running: ${label}…`);
    try {
      const res: any = await bulk({
        data: {
          ids,
          action,
          categoryId: action === "set_category" ? (arg ?? null) : null,
          collectionId: action === "add_to_collection" ? (arg ?? null) : null,
        },
      });
      toast.dismiss(t);
      const n = Number(res?.affected ?? 0);
      const blocked: Array<{ id: string; reasons: string[] }> = res?.blocked ?? [];
      // For publish: bucket blockers so the operator sees WHY nothing shipped.
      if (action === "publish" && blocked.length > 0) {
        const buckets: Record<string, number> = {};
        const bucketFor = (r: string) => {
          const s = r.toLowerCase();
          if (s.includes("timeline images") || s.includes("gallery")) return "Gallery incomplete";
          if (s.includes("body")) return "Missing / short body";
          if (s.includes("cover")) return "Missing cover";
          if (s.includes("category")) return "No category";
          if (s.includes("seo")) return "Missing SEO";
          if (s.includes("title")) return "Bad / empty title";
          if (s.includes("placeholder")) return "Placeholder text";
          if (s.includes("awaiting ai")) return "Awaiting AI review";
          if (s.includes("ai quality score")) return "Failed AI quality review";
          return "Other";
        };
        for (const b of blocked) {
          // count each row once by its primary (first) reason
          const key = bucketFor(b.reasons[0] ?? "Other");
          buckets[key] = (buckets[key] ?? 0) + 1;
        }
        const parts = Object.entries(buckets).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(" · ");
        toast.message(`Publish results: ✅ ${n} · ❌ ${blocked.length}`, { description: parts, duration: 12000 });
      } else if (n === 0) {
        toast.error("0 rows were written. Action did not persist.");
      } else {
        toast.success(`Done · ${n}/${ids.length} affected`);
      }
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin-content"] });
      qc.invalidateQueries({ queryKey: ["admin-collections"] });
      qc.invalidateQueries({ queryKey: ["homepage"] });
      qc.invalidateQueries({ queryKey: ["command-center"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); } finally { setBulkBusy(false); }
  }

  // Inline quick-fixes (deterministic, no AI cost) ---------------------------
  async function assignToCollection(d: DBDiscovery, collectionId: string) {
    const t = toast.loading("Adding to collection…");
    try {
      const res: any = await bulk({ data: { ids: [d.id], action: "add_to_collection", collectionId } });
      toast.dismiss(t);
      const n = Number(res?.affected ?? 0);
      if (n === 0) {
        toast.error("Did not persist - refresh and try again.");
        return;
      }
      toast.success("Added to collection");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin-content"] }),
        qc.invalidateQueries({ queryKey: ["admin-collections"] }),
        qc.invalidateQueries({ queryKey: ["homepage"] }),
        qc.invalidateQueries({ queryKey: ["command-center"] }),
        qc.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); }
  }
  async function fillSeo(d: DBDiscovery) {
    const title = (d.title ?? "").trim();
    if (!title) { toast.error("Cannot fill SEO: this discovery has no title yet."); return; }
    const seo_title = (d.seo_title?.trim() ? d.seo_title : title).slice(0, 65);
    const fallback = (d.excerpt ?? "").trim() || `${title} - explore on Smartasaurus.`;
    const seo_description = (d.seo_description?.trim() ? d.seo_description : fallback).slice(0, 175);
    const t = toast.loading("Filling SEO…");
    try {
      // upsert requires the full row shape (title is required, and Supabase upsert
      // replaces the row). Send every persisted field so nothing else gets nulled.
      await upsert({ data: {
        id: d.id,
        slug: d.slug,
        category_id: d.category_id ?? null,
        title,
        excerpt: d.excerpt ?? "",
        image_url: d.image_url ?? null,
        views_label: (d as any).views_label ?? null,
        is_featured: !!d.is_featured,
        is_daily: !!(d as any).is_daily,
        is_deep_dive: !!(d as any).is_deep_dive,
        is_published: d.is_published !== false,
        body: d.body ?? null,
        sort_order: (d as any).sort_order ?? 0,
        sources: Array.isArray((d as any).sources) ? (d as any).sources : [],
        confidence: (d as any).confidence ?? null,
        publish_at: (d as any).publish_at ?? null,
        seo_title,
        seo_description,
      } as any });
      toast.dismiss(t); toast.success("SEO filled");
      qc.invalidateQueries({ queryKey: ["admin-content"] });
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); }
  }
  // Rank collections by category match + title-keyword overlap with discovery.
  function rankCollectionsFor(d: DBDiscovery) {
    if (allCollections.length === 0) return [];
    const cat = catById.get(d.category_id ?? "");
    const catWords = cat ? cat.label.toLowerCase().split(/\s+/) : [];
    const titleWords = (d.title.toLowerCase().match(/[a-z]{4,}/g) ?? []);
    const ranked = allCollections.map((c) => {
      const t = c.title.toLowerCase();
      let score = 0;
      for (const w of catWords) if (w.length > 3 && t.includes(w)) score += 3;
      for (const w of titleWords) if (t.includes(w)) score += 1;
      return { c, score };
    }).sort((a, b) => b.score - a.score);
    return ranked;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-2xl uppercase">Discoveries</h2>
        <p className="text-xs text-muted-foreground">{discoveries.length} total · {counts.published} published · {counts.draft} unpublished · {snapshotLabel ? `snapshot ${snapshotLabel}` : "auto-refreshes every 30s"}</p>
      </div>

      <div className="grid gap-2 md:grid-cols-5">
        <DiscoveryMetric label="Total articles" value={discoveries.length} detail={`${counts.published} live`} />
        <DiscoveryMetric label="Unpublished" value={counts.draft} detail={counts.draft ? "visible + actionable" : "none"} tone={counts.draft ? "warn" : "ok"} />
        <DiscoveryMetric label="Article/reel links" value={reelCoverage?.articleOriginLinks ?? counts["has-reel"]} detail={`${reelCoverage?.uniqueOriginReels ?? counts["has-reel"]} unique reels`} />
        <DiscoveryMetric label="Imported reels" value={reelCoverage?.importedReels ?? 0} detail={`${reelCoverage?.linkedReels ?? 0} linked`} />
        <DiscoveryMetric label="Reels needing article" value={reelCoverage?.missingDiscoveryReels ?? 0} detail="from Instagram inbox" tone={(reelCoverage?.missingDiscoveryReels ?? 0) ? "warn" : "ok"} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-card/50 p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-9 pl-7" placeholder="Search title, slug, excerpt…" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(0); }}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="none">- No category -</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v: any) => setSort(v)}>
          <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="title">Title</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setEditing({ title: "", excerpt: "", sort_order: 0 })}>
          <Plus className="mr-2 h-4 w-4" /> New
        </Button>
      </div>

      {/* Saved views */}
      <div className="flex flex-wrap gap-1">
        {([
          { id: "all", label: "All" },
          { id: "published", label: "Published" },
          { id: "draft", label: "Drafts" },
          { id: "scheduled", label: "Scheduled" },
          { id: "missing", label: "Needs body" },
          { id: "needs-image", label: "Needs cover" },
          { id: "needs-meta", label: "Needs SEO" },
          { id: "needs-category", label: "Needs category" },
          { id: "not-in-collection", label: "Needs collection" },
          { id: "has-reel", label: "Matched reel" },
          { id: "no-reel", label: "No reel" },
        ] as { id: StatusFilter; label: string }[]).map((s) => (
          <button
            key={s.id}
            onClick={() => { setFilter(s.id); setPage(0); }}
            className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wider transition ${
              filter === s.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
            }`}
          >
            {s.label} ({counts[s.id]})
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <span className="text-xs font-medium">{selected.size} selected</span>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("publish")}>Publish</Button>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("unpublish")}>Unpublish</Button>
          <Select onValueChange={(v) => runBulk("set_category", v === "none" ? null : v)}>
            <SelectTrigger className="h-8 w-44"><SelectValue placeholder="Set category…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">- No category -</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => runBulk("add_to_collection", v)}>
            <SelectTrigger className="h-8 w-48"><SelectValue placeholder="Add to collection…" /></SelectTrigger>
            <SelectContent>
              {allCollections.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="destructive" disabled={bulkBusy} onClick={() => runBulk("delete")}>Delete</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {/* AI batch ops */}
      <div className="flex flex-wrap gap-2">
        {missingCount > 0 && (
          <Button variant="outline" size="sm" onClick={onBulk} disabled={bulkRunning}>
            <Sparkles className="mr-2 h-4 w-4" /> {bulkRunning ? "Generating…" : `Generate missing bodies (${missingCount})`}
          </Button>
        )}
        {missingCoverCount > 0 && (
          <Button variant="outline" size="sm" onClick={onBackfillCovers} disabled={coverBackfillRunning}>
            <ImageIcon className="mr-2 h-4 w-4" /> {coverBackfillRunning ? "Generating…" : `Backfill covers (${missingCoverCount})`}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border/60">
        <div className="grid grid-cols-12 items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-1 flex justify-center">
            <Checkbox checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.id))} onCheckedChange={toggleSelectAll} />
          </div>
          <div className="col-span-4">Title</div>
          <div className="col-span-2 hidden md:block">Category</div>
          <div className="col-span-2 hidden md:block">Reel match</div>
          <div className="col-span-1 hidden md:block">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-border">
          {pageRows.map((d) => {
            const status = articleStatus(d);
            const wc = countWords(d.body);
            const cat = catById.get(d.category_id ?? "");
            return (
              <div key={d.id} className="px-3 py-2 hover:bg-muted/20">
                <div className="grid grid-cols-12 items-center gap-2">
                  <div className="col-span-1 flex justify-center">
                    <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleSelect(d.id)} />
                  </div>
                  <div className="col-span-11 md:col-span-4 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium text-sm">{d.title}</p>
                      {!d.image_url && <ImageIcon className="h-3 w-3 shrink-0 text-amber-500" />}
                    </div>
                    <p className="truncate text-[10px] text-muted-foreground">
                      {d.slug} · {wc > 0 ? `${wc}w · ${readingMinutes(wc)}min` : "no body"}
                      {hasMatchedReel(d) ? " · reel linked" : " · no reel"}
                      {d.is_featured && " · featured"}
                      {d.is_daily && " · daily"}
                    </p>
                  </div>
                  <div className="col-span-2 hidden md:block min-w-0">
                    <span className="truncate text-xs text-muted-foreground">{cat?.label ?? "-"}</span>
                  </div>
                  <div className="col-span-2 hidden md:block min-w-0">
                    <ReelMatchBadge discovery={d} />
                  </div>
                  <div className="col-span-1 hidden md:block">
                    <StatusBadge status={status} />
                  </div>
                  <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-1">
                    <Button aria-label={d.image_url ? "Regenerate image" : "Generate image"} size="icon" variant="ghost" onClick={() => onGenerateImage(d)} disabled={imageId === d.id} title={d.image_url ? "Re-image" : "Generate image"}>
                      <ImageIcon className="h-3.5 w-3.5" />
                    </Button>
                    {status === "missing" ? (
                      <Button aria-label="Generate article body" size="icon" variant="ghost" onClick={() => onGenerate(d, false)} disabled={generatingId === d.id} title="Generate body">
                        <Sparkles className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button aria-label="Preview article" size="icon" variant="ghost" onClick={() => onPreview(d)} title="Preview">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button aria-label={`Edit ${d.title}`} size="icon" variant="ghost" onClick={() => setEditing(d)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button aria-label={`Delete ${d.title}`} size="icon" variant="ghost" onClick={() => { if (confirm(`Delete "${d.title}"? This cannot be undone.`)) remove.mutate(d.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {filter === "not-in-collection" && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Assign collection →</span>
                    {rankCollectionsFor(d).slice(0, 3).map(({ c, score }) => (
                      <Button key={c.id} size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => assignToCollection(d, c.id)}>
                        {c.title}{score > 0 && <span className="ml-1 text-muted-foreground">·{score}</span>}
                      </Button>
                    ))}
                    <Select onValueChange={(v) => assignToCollection(d, v)}>
                      <SelectTrigger className="h-6 w-32 text-[11px]"><SelectValue placeholder="More…" /></SelectTrigger>
                      <SelectContent>
                        {allCollections.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {filter === "needs-meta" && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">SEO →</span>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => fillSeo(d)}>
                      <Wand2 className="mr-1 h-3 w-3" /> Fill from title & excerpt
                    </Button>
                    <span className="text-[10px] text-muted-foreground truncate max-w-[60%]">→ "{(d.title ?? "").slice(0, 65)}"</span>
                  </div>
                )}
              </div>
            );
          })}
          {pageRows.length === 0 && (
            <PositiveEmpty filter={filter} />
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Page {safePage + 1} of {totalPages} · {filtered.length} results
          </p>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} discovery</DialogTitle></DialogHeader>
          {editing && (
            <DiscoveryForm initial={editing} categories={categories} onSubmit={(d: any, opts?: { silent?: boolean }) => save.mutate({ d, silent: opts?.silent })} busy={save.isPending} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={previewState.open} onOpenChange={(o) => !o && setPreviewState((p) => ({ ...p, open: false }))}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Preview - {previewState.title}</DialogTitle></DialogHeader>
          {previewState.loading ? (
            <p className="py-8 text-center text-muted-foreground">Generating…</p>
          ) : (
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm font-sans">{previewState.body}</pre>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PositiveEmpty({ filter }: { filter: StatusFilter }) {
  const msg: Record<StatusFilter, string> = {
    all: "No discoveries yet. Tap “New” to create your first.",
    published: "Nothing published yet - drafts will show up here once you publish.",
    draft: "No drafts in flight. Great - your queue is clean.",
    scheduled: "Nothing scheduled. Use the calendar to plan ahead.",
    missing: "Every discovery has a body. ✨",
    "needs-image": "Every published discovery has a cover. 🎨",
    "needs-meta": "Every published discovery has SEO. 🚀",
    "needs-category": "Every published discovery has a category. 📚",
    "not-in-collection": "Every published discovery lives in a collection. 🧩",
    "has-reel": "No discoveries are linked to reels yet.",
    "no-reel": "Every discovery has a matched reel.",
  };
  return (
    <div className="flex flex-col items-center gap-1 p-8 text-center">
      <p className="text-sm font-medium">{msg[filter]}</p>
      <p className="text-[11px] text-muted-foreground">Try another saved view to see more.</p>
    </div>
  );
}

function DiscoveryMetric({ label, value, detail, tone = "neutral" }: { label: string; value: number; detail: string; tone?: "neutral" | "ok" | "warn" }) {
  const toneClass = tone === "ok" ? "text-emerald-500" : tone === "warn" ? "text-amber-500" : "text-foreground";
  return (
    <div className="rounded-xl border border-border/60 bg-card/50 p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl ${toneClass}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{detail}</div>
    </div>
  );
}

function ReelMatchBadge({ discovery }: { discovery: AdminDiscovery }) {
  const reel = discovery.matched_reel;
  if (!hasMatchedReel(discovery)) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">No reel</span>;
  }
  const confidence = Math.round(Number(reel?.link_confidence ?? discovery.hq_confidence ?? 0) * 100);
  const label = confidence > 0 ? `Matched · ${confidence}%` : "Matched";
  if (reel?.permalink) {
    return (
      <a href={reel.permalink} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary hover:bg-primary/15">
        <Instagram className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">{label}</span>;
}

function StatusBadge({ status }: { status: "missing" | "draft" | "scheduled" | "published" }) {
  const map = {
    missing: "bg-destructive/15 text-destructive border-destructive/30",
    draft: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30",
    scheduled: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
    published: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  } as const;
  const label = { missing: "Missing", draft: "Draft", scheduled: "Scheduled", published: "Published" }[status];
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${map[status]}`}>{label}</span>;
}

// ============================================================
// DISCOVERY FORM (preserved)
// ============================================================
function DiscoveryForm({ initial, categories, onSubmit, busy }: any) {
  const genMeta = useServerFn(generateArticleMeta);
  const genFull = useServerFn(generateFullArticle);
  const [genBusy, setGenBusy] = useState(false);
  const [fullBusy, setFullBusy] = useState(false);
  const toLocalInput = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

  const [f, setF] = useState<any>({
    id: initial.id,
    title: initial.title ?? "",
    slug: initial.slug ?? "",
    excerpt: initial.excerpt ?? "",
    image_url: initial.image_url ?? "",
    views_label: initial.views_label ?? "",
    category_id: initial.category_id ?? null,
    is_featured: initial.is_featured ?? false,
    is_daily: initial.is_daily ?? false,
    is_deep_dive: initial.is_deep_dive ?? false,
    is_published: initial.is_published ?? true,
    body: initial.body ?? "",
    sort_order: initial.sort_order ?? 0,
    sources: Array.isArray(initial.sources) ? initial.sources : [],
    confidence: initial.confidence ?? null,
    publish_at: initial.publish_at ?? null,
    seo_title: initial.seo_title ?? "",
    seo_description: initial.seo_description ?? "",
  });
  const [publishAtLocal, setPublishAtLocal] = useState<string>(toLocalInput(initial.publish_at));
  const [sourcesText, setSourcesText] = useState<string>(
    (f.sources as Array<{ title: string; url: string }>).map((s) => `${s.title} | ${s.url}`).join("\n"),
  );
  const [imageValid, setImageValid] = useState(true);

  function parseSources(text: string) {
    return text.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
      const [titleRaw, ...rest] = line.split("|");
      const url = rest.join("|").trim();
      const title = (titleRaw ?? "").trim();
      if (!url && /^https?:\/\//i.test(title)) {
        try { return { title: new URL(title).hostname, url: title }; } catch { return null; }
      }
      if (!/^https?:\/\//i.test(url)) return null;
      return { title: title || (() => { try { return new URL(url).hostname; } catch { return url; } })(), url };
    }).filter(Boolean) as Array<{ title: string; url: string }>;
  }

  async function onGenerateSeo() {
    if (!f.title.trim()) { toast.error("Title required."); return; }
    setGenBusy(true);
    try {
      const cat = categories.find((c: DBCategory) => c.id === f.category_id)?.label ?? null;
      const r = await genMeta({ data: { title: f.title, excerpt: f.excerpt, category: cat } });
      setF((prev: any) => ({ ...prev, seo_title: r.seoTitle, seo_description: r.metaDescription, slug: prev.slug?.trim() ? prev.slug : r.slug }));
      toast.success("SEO generated");
    } catch (e: any) { toast.error(e.message); } finally { setGenBusy(false); }
  }
  async function onGenerateEverything() {
    if (!f.title.trim()) { toast.error("Title required."); return; }
    if (f.body?.trim() && !confirm("Overwrite body, sources, confidence, category, SEO?")) return;
    setFullBusy(true);
    const t = toast.loading("Generating full article (~15s)…");
    try {
      const cats = (categories as DBCategory[]).map((c) => ({ slug: c.slug, label: c.label }));
      const r = await genFull({ data: { title: f.title, excerpt: f.excerpt, categories: cats } });
      const matchedCat = (categories as DBCategory[]).find((c) => c.slug === r.categorySlug);
      setF((prev: any) => ({
        ...prev,
        body: r.body, seo_title: r.seoTitle, seo_description: r.metaDescription,
        confidence: r.confidence, category_id: matchedCat?.id ?? prev.category_id,
        slug: prev.slug?.trim() ? prev.slug : r.slug,
      }));
      setSourcesText(r.sources.map((s: any) => `${s.title} | ${s.url}`).join("\n"));
      toast.dismiss(t); toast.success("Full draft generated.");
    } catch (e: any) { toast.dismiss(t); toast.error(e.message); } finally { setFullBusy(false); }
  }

  function buildPayload() {
    return {
      ...f,
      sources: parseSources(sourcesText),
      publish_at: fromLocalInput(publishAtLocal),
      seo_title: f.seo_title?.trim() ? f.seo_title.trim() : null,
      seo_description: f.seo_description?.trim() ? f.seo_description.trim() : null,
      confidence: f.confidence || null,
    };
  }

  // Autosave (only for existing records, every 6s when dirty)
  const initialSnapshot = useMemo(() => JSON.stringify({ initial }), []);
  const [lastSaved, setLastSaved] = useState<string>(initialSnapshot);
  useEffect(() => {
    if (!f.id) return;
    const snap = JSON.stringify(f) + sourcesText + publishAtLocal;
    if (snap === lastSaved) return;
    const t = setTimeout(() => {
      if (!imageValid) return;
      onSubmit(buildPayload(), { silent: true });
      setLastSaved(snap);
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, sourcesText, publishAtLocal, imageValid]);

  // Cmd/Ctrl+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!imageValid) { toast.error("Fix image URL."); return; }
        onSubmit(buildPayload());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, sourcesText, publishAtLocal, imageValid]);


  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!imageValid) { toast.error("Fix image URL."); return; }
        onSubmit(buildPayload());
      }}
      className="space-y-3"
    >
      <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></Field>
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">One-click full draft</p>
            <p className="text-[11px] text-muted-foreground">Body, sources, confidence, category, SEO.</p>
          </div>
          <Button type="button" size="sm" onClick={onGenerateEverything} disabled={fullBusy || !f.title.trim()}>
            <Wand2 className="mr-1 h-4 w-4" />{fullBusy ? "…" : "Generate everything"}
          </Button>
        </div>
      </div>
      <Field label="Slug (blank = auto)"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="auto" /></Field>
      <Field label="Excerpt"><Textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} rows={2} /></Field>
      <ImageUrlField label="Image URL" value={f.image_url ?? ""} onChange={(v) => setF({ ...f, image_url: v })} onValidityChange={setImageValid} />
      <Field label="Category">
        <Select value={f.category_id ?? "none"} onValueChange={(v) => setF({ ...f, category_id: v === "none" ? null : v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">- None -</SelectItem>
            {categories.map((c: DBCategory) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Views label"><Input value={f.views_label ?? ""} onChange={(e) => setF({ ...f, views_label: e.target.value })} /></Field>
        <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Publish at (optional)">
          <Input type="datetime-local" value={publishAtLocal} onChange={(e) => setPublishAtLocal(e.target.value)} />
        </Field>
        <Field label="Confidence">
          <Select value={f.confidence ?? "none"} onValueChange={(v) => setF({ ...f, confidence: v === "none" ? null : v })}>
            <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">- Not set -</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="speculative">Speculative</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="rounded-lg border border-border p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">SEO metadata</p>
          <Button type="button" size="sm" variant="outline" onClick={onGenerateSeo} disabled={genBusy}>
            <Sparkles className="mr-1 h-3.5 w-3.5" />{genBusy ? "…" : "Generate"}
          </Button>
        </div>
        <Field label={`SEO title (${(f.seo_title ?? "").length}/70)`}>
          <Input value={f.seo_title ?? ""} maxLength={70} onChange={(e) => setF({ ...f, seo_title: e.target.value })} placeholder="Optional" />
        </Field>
        <Field label={`Meta description (${(f.seo_description ?? "").length}/180)`}>
          <Textarea value={f.seo_description ?? ""} maxLength={180} onChange={(e) => setF({ ...f, seo_description: e.target.value })} rows={2} placeholder="Optional" />
        </Field>
        <div className="mt-3">
          <SeoOgPreview
            title={f.title || ""}
            seoTitle={f.seo_title || ""}
            seoDescription={f.seo_description || f.excerpt || ""}
            slug={f.slug || "(auto)"}
            image={f.image_url || ""}
          />
        </div>
      </div>
      {f.title?.trim() && <RelatedSuggestionsBlock id={f.id} title={f.title} categoryId={f.category_id} />}
      {f.title?.trim() && (
        <StoryKit
          title={f.title}
          excerpt={f.excerpt}
          hook={f.reel_hook}
          body={f.body}
          slug={f.slug}
          imageUrl={f.image_url}
        />
      )}
      <Field label="Sources - one per line: Title | https://url">
        <Textarea value={sourcesText} onChange={(e) => setSourcesText(e.target.value)} rows={4} />
      </Field>
      <div className="grid grid-cols-4 gap-3">
        <Toggle label="Daily" v={f.is_daily} onChange={(v) => setF({ ...f, is_daily: v })} />
        <Toggle label="Featured" v={f.is_featured} onChange={(v) => setF({ ...f, is_featured: v })} />
        <Toggle label="Deep dive" v={f.is_deep_dive} onChange={(v) => setF({ ...f, is_deep_dive: v })} />
        <Toggle label="Published" v={f.is_published} onChange={(v) => setF({ ...f, is_published: v })} />
      </div>
      <Field label="Body">
        <Textarea value={f.body ?? ""} onChange={(e) => setF({ ...f, body: e.target.value })} rows={8} />
        <BodyMeter body={f.body ?? ""} />
      </Field>
      <DialogFooter><Button type="submit" disabled={busy || !imageValid}>{busy ? "Saving…" : "Save"}</Button></DialogFooter>
    </form>
  );
}

// ============================================================
// CATEGORIES - with discovery counts + views
// ============================================================
function CategoriesPanel({ categories, discoveries }: { categories: DBCategory[]; discoveries: DBDiscovery[] }) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertCategory);
  const del = useServerFn(deleteCategory);
  const [editing, setEditing] = useState<Partial<DBCategory> | null>(null);

  const save = useMutation({
    mutationFn: (d: any) => upsert({ data: d }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Saved"); setEditing(null); },
    onError: (e: any) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e.message),
  });

  const countByCat = useMemo(() => {
    const m = new Map<string, { total: number; published: number }>();
    discoveries.forEach((d) => {
      if (!d.category_id) return;
      const e = m.get(d.category_id) ?? { total: 0, published: 0 };
      e.total += 1;
      if (d.is_published) e.published += 1;
      m.set(d.category_id, e);
    });
    return m;
  }, [discoveries]);

  // Fallback thumbnail per category: most recent discovery's image_url
  const previewByCat = useMemo(() => {
    const m = new Map<string, string>();
    const sorted = [...discoveries].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    sorted.forEach((d) => {
      if (!d.category_id || !d.image_url) return;
      if (!m.has(d.category_id)) m.set(d.category_id, d.image_url);
    });
    return m;
  }, [discoveries]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl uppercase">Categories</h2>
          <p className="text-xs text-muted-foreground">{categories.length} categories</p>
        </div>
        <Button onClick={() => setEditing({ slug: "", label: "", title: "", blurb: "", sort_order: 0 })}>
          <Plus className="mr-2 h-4 w-4" /> New category
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {categories.map((c) => {
          const stats = countByCat.get(c.id) ?? { total: 0, published: 0 };
          return (
            <div key={c.id} className="rounded-xl border border-border/60 bg-card/50 p-3">
              <div className="flex gap-3">
                {c.cover_url ? (
                  <img src={c.cover_url} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
                ) : previewByCat.get(c.id) ? (
                  <div className="relative h-16 w-16 shrink-0">
                    <img src={previewByCat.get(c.id)!} alt="" className="h-16 w-16 rounded-md object-cover opacity-80" />
                    <span className="absolute -bottom-1 -right-1 rounded-full bg-amber-500/90 px-1 text-[8px] font-semibold uppercase tracking-wider text-white">auto</span>
                  </div>
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-dashed border-border bg-muted/30">
                    <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{c.label}</p>
                  <p className="truncate text-xs text-muted-foreground">/category/{c.slug}</p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    <span>{stats.published} pub</span>
                    <span>·</span>
                    <span>{stats.total} total</span>
                  </div>
                </div>
              </div>
              {c.blurb && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.blurb}</p>}
              <div className="mt-3 flex items-center justify-end gap-1">
                <Button asChild size="sm" variant="ghost">
                  <Link to="/category/$slug" params={{ slug: c.slug }} target="_blank">Open</Link>
                </Button>
                <Button aria-label={`Edit ${c.label}`} size="icon" variant="ghost" onClick={() => setEditing(c)}><Pencil className="h-4 w-4" /></Button>
                <Button aria-label={`Delete category ${c.label}`} size="icon" variant="ghost" onClick={() => { if (confirm(`Delete category "${c.label}"? Discoveries will be uncategorized.`)) remove.mutate(c.id); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} category</DialogTitle></DialogHeader>
          {editing && <CategoryForm initial={editing} onSubmit={(d: any) => save.mutate(d)} busy={save.isPending} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryForm({ initial, onSubmit, busy }: any) {
  const [f, setF] = useState<any>({
    id: initial.id, slug: initial.slug ?? "", label: initial.label ?? "", title: initial.title ?? "",
    blurb: initial.blurb ?? "", cover_url: initial.cover_url ?? "", sort_order: initial.sort_order ?? 0,
  });
  const [imageValid, setImageValid] = useState(true);
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!imageValid) { toast.error("Fix cover URL."); return; } onSubmit(f); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></Field>
        <Field label="Label"><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} required /></Field>
      </div>
      <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></Field>
      <Field label="Blurb"><Textarea value={f.blurb} onChange={(e) => setF({ ...f, blurb: e.target.value })} rows={2} /></Field>
      <ImageUrlField label="Cover URL" value={f.cover_url ?? ""} onChange={(v) => setF({ ...f, cover_url: v })} onValidityChange={setImageValid} />
      <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} /></Field>
      <DialogFooter><Button type="submit" disabled={busy || !imageValid}>{busy ? "…" : "Save"}</Button></DialogFooter>
    </form>
  );
}

// ============================================================
// QUIZZES - with search, filter, linked discovery
// ============================================================
function QuizzesPanel({ quizzes, discoveries }: { quizzes: DBQuiz[]; discoveries: DBDiscovery[] }) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertQuiz);
  const del = useServerFn(deleteQuiz);
  const [editing, setEditing] = useState<Partial<DBQuiz> | null>(null);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | "guess" | "challenge">("all");

  const save = useMutation({
    mutationFn: (d: any) => upsert({ data: d }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Saved"); setEditing(null); },
    onError: (e: any) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-content"] }); qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e.message),
  });

  const filtered = quizzes.filter((q2) => {
    if (kindFilter !== "all" && q2.kind !== kindFilter) return false;
    if (q.trim() && !q2.question.toLowerCase().includes(q.toLowerCase()) && !q2.slug.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-2xl uppercase">Quizzes</h2>
        <p className="text-xs text-muted-foreground">{quizzes.length} quizzes</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-card/50 p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-9 pl-7" placeholder="Search question or slug…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={kindFilter} onValueChange={(v: any) => setKindFilter(v)}>
          <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="guess">Guess</SelectItem>
            <SelectItem value="challenge">Challenge</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setEditing({ slug: "", kind: "guess", question: "", options: ["", ""], answer_index: 0, explanation: "", sort_order: 0 })}>
          <Plus className="mr-2 h-4 w-4" /> New quiz
        </Button>
      </div>

      {(() => {
        // Group by placement → fallback "Unplaced"
        const groups = new Map<string, DBQuiz[]>();
        filtered.forEach((qz) => {
          const key = qz.placement?.trim() ? qz.placement : "- Unplaced";
          const arr = groups.get(key) ?? [];
          arr.push(qz);
          groups.set(key, arr);
        });
        const entries = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
        if (entries.length === 0) {
          return <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No quizzes match.</p>;
        }
        return (
          <div className="space-y-2">
            {entries.map(([group, items]) => (
              <details key={group} open className="rounded-lg border border-border/60 bg-card/30">
                <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted/30">
                  <span>{group.replace(/-/g, " ")}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-foreground">{items.length}</span>
                </summary>
                <div className="space-y-1 border-t border-border/40 p-2">
                  {items.map((q2) => (
                    <div key={q2.id} className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-card/50 p-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{q2.question}</p>
                        <p className="text-[11px] text-muted-foreground">{q2.kind} · {q2.options.length} options · answer #{q2.answer_index + 1}</p>
                      </div>
                      <Button aria-label="Edit quiz" size="icon" variant="ghost" onClick={() => setEditing(q2)}><Pencil className="h-4 w-4" /></Button>
                      <Button aria-label="Delete quiz" size="icon" variant="ghost" onClick={() => { if (confirm(`Delete quiz "${q2.question.slice(0, 60)}…"?`)) remove.mutate(q2.id); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        );
      })()}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} quiz</DialogTitle></DialogHeader>
          {editing && <QuizForm initial={editing} onSubmit={(d: any) => save.mutate(d)} busy={save.isPending} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuizForm({ initial, onSubmit, busy }: any) {
  const [f, setF] = useState<any>({
    id: initial.id, slug: initial.slug ?? "", kind: initial.kind ?? "guess", question: initial.question ?? "",
    options: Array.isArray(initial.options) ? initial.options : ["", ""],
    answer_index: initial.answer_index ?? 0, explanation: initial.explanation ?? "",
    href: initial.href ?? "", placement: initial.placement ?? "", sort_order: initial.sort_order ?? 0,
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, options: f.options.filter((o: string) => o.trim()) }); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></Field>
        <Field label="Kind">
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="guess">Guess</SelectItem>
              <SelectItem value="challenge">Challenge</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Question"><Textarea value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} rows={2} required /></Field>
      <div>
        <Label>Options</Label>
        <div className="mt-1 space-y-2">
          {f.options.map((o: string, i: number) => (
            <div key={i} className="flex gap-2">
              <Input value={o} onChange={(e) => { const n = [...f.options]; n[i] = e.target.value; setF({ ...f, options: n }); }} placeholder={`Option ${i + 1}`} />
              <Button type="button" variant="ghost" size="sm" onClick={() => setF({ ...f, options: f.options.filter((_: any, j: number) => j !== i) })}>×</Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, options: [...f.options, ""] })}>+ Add option</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Answer index (0-based)"><Input type="number" min={0} value={f.answer_index} onChange={(e) => setF({ ...f, answer_index: Number(e.target.value) })} /></Field>
        <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} /></Field>
      </div>
      <Field label="Explanation"><Textarea value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} rows={2} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Href"><Input value={f.href ?? ""} onChange={(e) => setF({ ...f, href: e.target.value })} /></Field>
        <Field label="Placement">
          <Select value={f.placement ?? "none"} onValueChange={(v) => setF({ ...f, placement: v === "none" ? null : v })}>
            <SelectTrigger><SelectValue placeholder="Where" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">- None -</SelectItem>
              <SelectItem value="before-weird-animals">Before Weird Animals</SelectItem>
              <SelectItem value="after-weird-animals">After Weird Animals</SelectItem>
              <SelectItem value="before-space">Before Space</SelectItem>
              <SelectItem value="before-body">Before Body</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <DialogFooter><Button type="submit" disabled={busy}>{busy ? "…" : "Save"}</Button></DialogFooter>
    </form>
  );
}

// ============================================================
// AUDIENCE (Subscribers + Demand)
// ============================================================
function AudiencePanel() {
  const [tab, setTab] = useState<"subscribers" | "demand">("subscribers");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl uppercase">Audience</h2>
          <p className="text-xs text-muted-foreground">Subscribers and product demand</p>
        </div>
        <div className="flex rounded-md border border-border bg-card/50 p-0.5">
          <button onClick={() => setTab("subscribers")} className={`rounded px-3 py-1 text-xs font-medium ${tab === "subscribers" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Subscribers</button>
          <button onClick={() => setTab("demand")} className={`rounded px-3 py-1 text-xs font-medium ${tab === "demand" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Demand</button>
        </div>
      </div>
      {tab === "subscribers" ? <SubscribersPanel /> : <DemandPanel />}
    </div>
  );
}

function SubscribersPanel() {
  const fetchSubs = useServerFn(listSubscribers);
  const sendIssue = useServerFn(sendNewsletterIssue);
  const { data, isLoading, error } = useQuery({ queryKey: ["subscribers", "external-only-v2"], queryFn: () => fetchSubs() });
  const [subject, setSubject] = useState("");
  const [preview, setPreview] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");

  function exportCsv(subs: Subscriber[]) {
    const header = "email,subscribed_at\n";
    const rows = subs.map((s) => `${escapeCsv(s.email)},${escapeCsv(s.created_at)}`).join("\n");
    const blob = new Blob([header + rows + "\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }

  async function onSend() {
    if (subject.trim().length < 3 || bodyText.trim().length < 10) { toast.error("Subject and body required."); return; }
    const count = data?.subscribers.length ?? 0;
    if (!confirm(`Send to ${count} subscribers?`)) return;
    setSending(true);
    try {
      const res = await sendIssue({ data: { subject: subject.trim(), bodyText: bodyText.trim(), preview: preview.trim() || undefined } });
      toast.success(`Queued ${res.queued} · skipped ${res.skipped} · failed ${res.failed}`);
      setSubject(""); setPreview(""); setBodyText("");
    } catch (e: any) { toast.error(e?.message ?? "Failed."); } finally { setSending(false); }
  }

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-destructive">Failed to load.</p>;
  const subs = data?.subscribers ?? [];
  const filtered = q.trim() ? subs.filter((s) => s.email.toLowerCase().includes(q.toLowerCase())) : subs;

  return (
    <div className="space-y-6">
      <SubscribersGrowthChart />

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{subs.length}</span> real subscribers{(data as any)?.internalCount ? ` · ${(data as any).internalCount} internal hidden` : ""}</p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input className="h-9 w-56 pl-7" placeholder="Search email…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button onClick={() => exportCsv(subs)} disabled={subs.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No real subscribers yet. Internal founder/QA emails are hidden from this list and excluded from sends.</p>
        ) : (
          <div className="max-h-96 overflow-auto rounded-lg border border-border">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="sticky top-0 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Email</th>
                  <th className="px-4 py-2 text-left font-semibold">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-4 py-2 font-mono text-xs">{s.email}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 500 && <p className="border-t border-border p-2 text-center text-xs text-muted-foreground">Showing 500 of {filtered.length}</p>}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card/50 p-4">
        <h3 className="text-lg font-semibold">Compose newsletter</h3>
        <p className="mt-1 text-xs text-muted-foreground">Sent only to real subscribers. Internal founder/QA emails are excluded.</p>
        <div className="mt-4 space-y-3">
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} /></div>
          <div><Label>Preview text (optional)</Label><Input value={preview} onChange={(e) => setPreview(e.target.value)} disabled={sending} /></div>
          <div><Label>Body</Label><Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} disabled={sending} /></div>
          <div className="flex justify-end">
            <Button onClick={onSend} disabled={sending || subs.length === 0}>{sending ? "Sending…" : `Send to ${subs.length}`}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function escapeCsv(v: string) { if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`; return v; }

function DemandPanel() {
  const fetchStats = useServerFn(getWaitlistStats);
  const { data, isLoading, error, refetch, isFetching } = useQuery<WaitlistStats>({ queryKey: ["admin", "waitlist-stats"], queryFn: () => fetchStats() });
  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-red-600">Failed: {(error as Error).message}</p>;
  if (!data) return null;
  const { rows, totalSignups, uniqueEmails, mostRequested } = data;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl uppercase">Product Demand</h3>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RotateCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card/50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total signups</div>
          <div className="mt-1 font-display text-3xl">{totalSignups.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-border bg-card/50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Unique emails</div>
          <div className="mt-1 font-display text-3xl">{uniqueEmails.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-border bg-card/50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Most requested</div>
          <div className="mt-1 font-display text-base leading-tight">{mostRequested ?? "-"}</div>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Product</th>
              <th className="px-4 py-3 text-right font-bold">Count</th>
              <th className="px-4 py-3 text-right font-bold">Share</th>
              <th className="px-4 py-3 text-left font-bold">Distribution</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No waitlist yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.product_name} className="border-t border-border">
                <td className="px-4 py-3">{r.product_name}</td>
                <td className="px-4 py-3 text-right font-semibold">{r.count.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">{(r.share * 100).toFixed(1)}%</td>
                <td className="px-4 py-3">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${Math.max(2, r.share * 100)}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// SHARED
// ============================================================
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><Label className="mb-1 block">{label}</Label>{children}</div>;
}
function Toggle({ label, v, onChange }: { label: string; v: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={v} onCheckedChange={onChange} />
    </label>
  );
}

function RelatedSuggestionsBlock({ id, title, categoryId }: { id?: string; title: string; categoryId?: string | null }) {
  const suggest = useServerFn(suggestRelated);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "related", id ?? title, categoryId ?? ""],
    queryFn: () => suggest({ data: { discoveryId: id, title, categoryId } }),
    staleTime: 60_000,
    enabled: !!title.trim(),
  });
  if (isLoading) return <div className="rounded-lg border border-border p-3"><Skeleton className="h-16 w-full" /></div>;
  const items = (data?.related ?? []) as Array<{ id: string; title: string; slug: string }>;
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Related discoveries</p>
      <ul className="space-y-1 text-sm">
        {items.slice(0, 6).map((d) => <li key={d.id}>· {d.title}</li>)}
      </ul>
    </div>
  );
}
